import memoizeOne from 'memoize-one';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import {
	mutateSingleUserPicker,
	mutateMultiUserPicker,
	mapUserOptionsToIds,
	mapUserOptionsToAccountIds,
} from '@atlassian/jira-issue-create-commons/src/common/ui/fields/base-fields/user-picker-field/utils.tsx';
import { transformJSONStringToObject } from '@atlassian/jira-issue-create-commons/src/common/utils/transform-to-object/index.tsx';
import AffectedServicesField from '@atlassian/jira-issue-create-commons/src/ui/affected-services-field/index.tsx';
import { mutateAffectedServicesField } from '@atlassian/jira-issue-create-commons/src/ui/affected-services-field/utils.tsx';
import AssetField from '@atlassian/jira-issue-create-commons/src/ui/asset-field/index.tsx';
import {
	mutateAssetsFields,
	mapAssetOptionsToAssetEntity,
} from '@atlassian/jira-issue-create-commons/src/ui/asset-field/utils.tsx';
import AssigneeField from '@atlassian/jira-issue-create-commons/src/ui/assignee-field/index.tsx';
import {
	mapUserToAssigneeValue,
	mutateAssigneeUserPicker,
} from '@atlassian/jira-issue-create-commons/src/ui/assignee-field/utils.tsx';
import AtlassianTeamsField from '@atlassian/jira-issue-create-commons/src/ui/atlassian-teams-field/index.tsx';
import {
	transformAtlassianTeamFieldTypeString,
	mutateAtlassianTeamField,
} from '@atlassian/jira-issue-create-commons/src/ui/atlassian-teams-field/utils.tsx';
import { getFileIdsFromFiles } from '@atlassian/jira-issue-create-commons/src/ui/attachments-field/utils.tsx';
import CascadingSelectField from '@atlassian/jira-issue-create-commons/src/ui/cascading-select-field/index.tsx';
import { mutateCascadingSelectField } from '@atlassian/jira-issue-create-commons/src/ui/cascading-select-field/utils.tsx';
import CheckboxField from '@atlassian/jira-issue-create-commons/src/ui/checkbox-field/index.tsx';
import { mutateCheckboxField } from '@atlassian/jira-issue-create-commons/src/ui/checkbox-field/utils.tsx';
import CmdbField from '@atlassian/jira-issue-create-commons/src/ui/cmdb-field/index.tsx';
import { transformCmdbField } from '@atlassian/jira-issue-create-commons/src/ui/cmdb-field/utils.tsx';
import ColorField from '@atlassian/jira-issue-create-commons/src/ui/color-field/index.tsx';
import {
	mutateIssueColorField,
	mutateEpicColorField,
} from '@atlassian/jira-issue-create-commons/src/ui/color-field/utils.tsx';
import ComponentsField from '@atlassian/jira-issue-create-commons/src/ui/components-field/index.tsx';
import { mutateComponentsField } from '@atlassian/jira-issue-create-commons/src/ui/components-field/utils.tsx';
import CustomTeamsField from '@atlassian/jira-issue-create-commons/src/ui/custom-teams-field/index.tsx';
import { mutateTeamField } from '@atlassian/jira-issue-create-commons/src/ui/custom-teams-field/utils.tsx';
import DataClassificationField from '@atlassian/jira-issue-create-commons/src/ui/data-classification-field/index.tsx';
import DateField from '@atlassian/jira-issue-create-commons/src/ui/date-field/index.tsx';
import {
	transformDate,
	mutateDateField,
} from '@atlassian/jira-issue-create-commons/src/ui/date-field/utils.tsx';
import DateTimeField from '@atlassian/jira-issue-create-commons/src/ui/date-time-field/index.tsx';
import { mutateDateTimeField } from '@atlassian/jira-issue-create-commons/src/ui/date-time-field/utils.tsx';
import { mutateEpicLinkField } from '@atlassian/jira-issue-create-commons/src/ui/epic-link-field/utils.tsx';
import IssueLinksField from '@atlassian/jira-issue-create-commons/src/ui/issue-links-field/index.tsx';
import { mutateIssueLinksField } from '@atlassian/jira-issue-create-commons/src/ui/issue-links-field/utils.tsx';
import LabelsField, {
	ForgeLabelsField,
} from '@atlassian/jira-issue-create-commons/src/ui/labels-field/index.tsx';
import { mutateLabelsFields } from '@atlassian/jira-issue-create-commons/src/ui/labels-field/utils.tsx';
import { getLogWorkSubmitValue } from '@atlassian/jira-issue-create-commons/src/ui/log-work-field/utils.tsx';
import MessageField from '@atlassian/jira-issue-create-commons/src/ui/message-field/index.tsx';
import MultiGroupPickerField from '@atlassian/jira-issue-create-commons/src/ui/multi-grouppicker-field/index.tsx';
import { mutateMultipleGroupPickerField } from '@atlassian/jira-issue-create-commons/src/ui/multi-grouppicker-field/utils.tsx';
import MultiSelectField from '@atlassian/jira-issue-create-commons/src/ui/multi-select-field/index.tsx';
import {
	mutateMultiSelectField,
	mapMultiSelectOptionsToConnectData,
} from '@atlassian/jira-issue-create-commons/src/ui/multi-select-field/utils.tsx';
import MultiUserPickerField from '@atlassian/jira-issue-create-commons/src/ui/multi-userpicker-field/index.tsx';
import MultiVersionsField from '@atlassian/jira-issue-create-commons/src/ui/multi-versions-field/index.tsx';
import { mutateMultiVersionsField } from '@atlassian/jira-issue-create-commons/src/ui/multi-versions-field/utils.tsx';
import NumberField from '@atlassian/jira-issue-create-commons/src/ui/number-field/index.tsx';
import {
	transformAsNumber,
	mutateNumberField,
} from '@atlassian/jira-issue-create-commons/src/ui/number-field/utils.tsx';
import OrganizationsField from '@atlassian/jira-issue-create-commons/src/ui/organizations-field/index.tsx';
import { mutateOrganizationsField } from '@atlassian/jira-issue-create-commons/src/ui/organizations-field/utils.tsx';
import ParentField from '@atlassian/jira-issue-create-commons/src/ui/parent-field/index.tsx';
import { mutateParentField } from '@atlassian/jira-issue-create-commons/src/ui/parent-field/utils.tsx';
import PeopleField from '@atlassian/jira-issue-create-commons/src/ui/people-field/index.tsx';
import {
	mapUserOptionOrOptionsToIds,
	mutatePeopleField,
} from '@atlassian/jira-issue-create-commons/src/ui/people-field/utils.tsx';
import PriorityField from '@atlassian/jira-issue-create-commons/src/ui/priority-field/index.tsx';
import { mutatePriorityField } from '@atlassian/jira-issue-create-commons/src/ui/priority-field/utils.tsx';
import ProjectField from '@atlassian/jira-issue-create-commons/src/ui/project-field/index.tsx';
import { mutateProjectField } from '@atlassian/jira-issue-create-commons/src/ui/project-field/utils.tsx';
import RadioGroupField from '@atlassian/jira-issue-create-commons/src/ui/radio-group-field/index.tsx';
import ReporterField from '@atlassian/jira-issue-create-commons/src/ui/reporter-field/index.tsx';
import ResolutionField from '@atlassian/jira-issue-create-commons/src/ui/resolution-field/index.tsx';
import { mutateResolutionField } from '@atlassian/jira-issue-create-commons/src/ui/resolution-field/utils.tsx';
import RespondersField from '@atlassian/jira-issue-create-commons/src/ui/responders-field/index.tsx';
import { mapUserOptionsToAris } from '@atlassian/jira-issue-create-commons/src/ui/responders-field/utils.tsx';
import SingleGroupPickerField from '@atlassian/jira-issue-create-commons/src/ui/single-grouppicker-field/index.tsx';
import { mutateSingleGroupPickerField } from '@atlassian/jira-issue-create-commons/src/ui/single-grouppicker-field/utils.tsx';
import SingleSelectField from '@atlassian/jira-issue-create-commons/src/ui/single-select-field/index.tsx';
import {
	mutateSingleSelectField,
	mapSelectOptionsToConnectData,
} from '@atlassian/jira-issue-create-commons/src/ui/single-select-field/utils.tsx';
import SingleUserPickerField from '@atlassian/jira-issue-create-commons/src/ui/single-userpicker-field/index.tsx';
import SingleVersionField from '@atlassian/jira-issue-create-commons/src/ui/single-version-field/index.tsx';
import { mutateSingleVersionField } from '@atlassian/jira-issue-create-commons/src/ui/single-version-field/utils.tsx';
import SprintField from '@atlassian/jira-issue-create-commons/src/ui/sprint-field/index.tsx';
import { mutateJiraSprintField } from '@atlassian/jira-issue-create-commons/src/ui/sprint-field/utils.tsx';
import TextField from '@atlassian/jira-issue-create-commons/src/ui/text-field/index.tsx';
import { mutateTextField } from '@atlassian/jira-issue-create-commons/src/ui/text-field/utils.tsx';
import TextAreaField from '@atlassian/jira-issue-create-commons/src/ui/textarea-field/index.tsx';
import { mutateTextAreaField } from '@atlassian/jira-issue-create-commons/src/ui/textarea-field/utils.tsx';
import UrlField from '@atlassian/jira-issue-create-commons/src/ui/url-field/index.tsx';
import { mutateUrlField } from '@atlassian/jira-issue-create-commons/src/ui/url-field/utils.tsx';
import {
	asClassicEpicColor,
	asServerColor,
} from '@atlassian/jira-issue-epic-color/src/common/utils.tsx';
import { mapOptionsToServiceEntities } from '@atlassian/jira-issue-field-affected-services/src/ui/edit/utils.tsx';
import { transformToServerValue } from '@atlassian/jira-issue-field-cascading-select/src/common/utils.tsx';
import { mapOptionsToData as mapCheckboxOptionsToData } from '@atlassian/jira-issue-field-checkbox/src/ui/edit/utils.tsx';
import { mapOptionsToData as mapComponentOptionToData } from '@atlassian/jira-issue-field-components-field/src/ui/edit/utils.tsx';
import { transformTeamFieldTypeString } from '@atlassian/jira-issue-field-custom-team/src/ui/edit/utils.tsx';
import { mapEpicLinkOptionToData } from '@atlassian/jira-issue-field-epic-link/src/ui/edit/utils.tsx';
import {
	transformGroupToName,
	transformGroupsToNames,
} from '@atlassian/jira-issue-field-group-picker/src/common/utils/index.tsx';
import { mapOptionsToData } from '@atlassian/jira-issue-field-issue-links/src/ui/edit/utils.tsx';
import { mapLabelOptionsToData } from '@atlassian/jira-issue-field-labels/src/common/utils.tsx';
import { mapOrganizationOptionsToData } from '@atlassian/jira-issue-field-organizations/src/ui/edit/utils.tsx';
import { mapOptionToData as mapParentLinkOptionToData } from '@atlassian/jira-issue-field-parent-link/src/common/utils.tsx';
import { mapOptionToData as mapParentOptionToData } from '@atlassian/jira-issue-field-parent/src/common/utils.tsx';
import { mapOptionToData as mapPriorityOptionToData } from '@atlassian/jira-issue-field-priority/src/ui/edit/utils.tsx';
import { mapOptionToData as mapProjectOptionToData } from '@atlassian/jira-issue-field-project/src/common/utils.tsx';
import { mapOptionToData as mapRadioGroupOptionToData } from '@atlassian/jira-issue-field-radio-group/src/ui/edit/utils.tsx';
import { mapOptionToResolutionOption } from '@atlassian/jira-issue-field-resolution/src/common/utils.tsx';
import { mapRoleToRestrictionValue } from '@atlassian/jira-issue-field-role-picker/src/ui/edit/utils.tsx';
import {
	mapSelectOptionsToData,
	mapMultiSelectOptionsToData,
} from '@atlassian/jira-issue-field-select/src/ui/edit/utils.tsx';
import { mapSprintOptionToData } from '@atlassian/jira-issue-field-sprint/src/common/utils.tsx';
import { convertTextToAdf } from '@atlassian/jira-issue-field-textarea/src/ui/edit/utils.tsx';
import {
	getVersionsIds,
	getVersionId,
} from '@atlassian/jira-issue-field-versions/src/ui/edit/utils.tsx';
import {
	ISSUE_LINKS_TYPE,
	WORK_LOG_TYPE,
	ATTACHMENT_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { IssueCreateOrganizationField as OrganizationField } from '@atlassian/jira-servicedesk-customer-service-custom-fields/src/ui/issue-create-organization-field/index.tsx';
import { mapOrganizationOptionToData } from '@atlassian/jira-servicedesk-customer-service-custom-fields/src/ui/issue-create-organization-field/utils.tsx';
import { IssueCreateEntitlementField as EntitlementField } from '@atlassian/jira-servicedesk-customer-service-entitlements-field/src/ui/issue-create-entitlement-field/index.tsx';
import { mapEntitlementOptionToData } from '@atlassian/jira-servicedesk-customer-service-entitlements-field/src/ui/issue-create-entitlement-field/utils.tsx';
import AttachmentsField from '../ui/fields/attachments-field/index.tsx';
import DescriptionField from '../ui/fields/description-field/index.tsx';
import { mutateADFField, sanitizeADFField } from '../ui/fields/description-field/utils.tsx';
import { EpicLinkField } from '../ui/fields/epic-link-field/index.tsx';
import IssueRestrictionField from '../ui/fields/issue-restriction/index.tsx';
import LogWorkField from '../ui/fields/log-work-field/index.tsx';
import { ParentLinkField } from '../ui/fields/parent-link-field/index.tsx';
import SecurityLevelField from '../ui/fields/security-level-field/index.tsx';
import {
	transformOnSubmit,
	mutateSecurityLevelField,
} from '../ui/fields/security-level-field/utils.tsx';
import TimeTrackingField from '../ui/fields/time-tracking-field/index.tsx';
import {
	transformTimeTrackingValueToSubmitFormat,
	mutateTimeTrackingField,
} from '../ui/fields/time-tracking-field/utils.tsx';
import {
	PROJECT_FIELD,
	ISSUE_TYPE_FIELD,
	REQUEST_TYPE_FIELD,
	TEXT_FIELD,
	RICH_TEXT_FIELD,
	COMPONENTS_FIELD,
	DATA_CLASSIFICATION_FIELD,
	USER_PICKER_FIELD,
	NUMBER_FIELD,
	DATE_TIME_FIELD,
	DATE_PICKER_FIELD,
	EPIC_LINK_FIELD,
	LABEL_FIELD,
	ASSET_FIELD,
	VERSION_FIELD,
	MULTI_VERSION_FIELD,
	PRIORITY_FIELD,
	MULTI_USER_PICKER_FIELD,
	SECURITY_FIELD,
	URL_FIELD,
	CHECKBOX_FIELD,
	RADIO_FIELD,
	SINGLE_SELECT_FIELD,
	MULTI_SELECT_FIELD,
	SINGLE_GROUP_PICKER_FIELD,
	MUTLI_GROUP_PICKER_FIELD,
	SPRINT_FIELD,
	ISSUE_LINK_FIELD,
	COLOR_FIELD,
	ASSIGNEE_FIELD,
	REPORTER_FIELD,
	TEXT_AREA_FIELD,
	EPIC_COLOR_FIELD,
	ISSUE_RESTRICTION_FIELD,
	RESOLUTION_FIELD,
	ATTACHMENTS_FIELD,
	CASCADING_SELECT_FIELD,
	PEOPLE_FIELD,
	ORGANIZATIONS_FIELD,
	ATLASSIAN_TEAM_FIELD,
	AFFECTED_SERVICES_FIELD,
	CMDB_FIELD,
	CONNECT_TEXT_FIELD,
	CONNECT_NUMBER_FIELD,
	CONNECT_SINGLE_SELECT_FIELD,
	CONNECT_MULTI_SELECT_FIELD,
	FORGE_STRING_FIELD,
	FORGE_NUMBER_FIELD,
	FORGE_USER_PICKER_FIELD,
	FORGE_MULTI_USER_PICKER_FIELD,
	FORGE_GROUP_PICKER_FIELD,
	FORGE_MULTI_GROUP_PICKER_FIELD,
	FORGE_STRINGS_FIELD,
	MESSAGE_EDIT_FIELD,
	TIME_TRACKING_FIELD,
	JSM_MULTI_USER_PICKER_FIELD,
	JSM_PEOPLE_FIELD,
	TEAM_FIELD,
	FORGE_DATETIME_FIELD,
	FORGE_DATE_FIELD,
	FORGE_OBJECT_FIELD,
	LOG_WORK_FIELD,
	PARENT_LINK_FIELD,
	PARENT_FIELD,
	RESPONDERS_FIELD,
	ENTITLEMENT_FIELD,
	ORGANIZATION_FIELD,
} from './graphql-field-types.tsx';

const createFieldMapItems = () =>
	({
		[AFFECTED_SERVICES_FIELD]: {
			Component: AffectedServicesField,
			defaultValueKey: 'selectedAffectedServices',
			submitTransform: mapOptionsToServiceEntities,
			retainUserInputTransform: mutateAffectedServicesField,
		},
		[TEXT_FIELD]: {
			Component: TextField,
			defaultValueKey: 'text',
			retainUserInputTransform: mutateTextField,
		},
		[CONNECT_TEXT_FIELD]: {
			Component: TextField,
			defaultValueKey: 'text',
			retainUserInputTransform: mutateTextField,
		},
		[FORGE_STRING_FIELD]: {
			Component: TextField,
			defaultValueKey: 'text',
			retainUserInputTransform: mutateTextField,
		},
		[RICH_TEXT_FIELD]: {
			Component: DescriptionField,
			defaultValueKey: 'richText.adfValue.json',
			submitTransform: sanitizeADFField,
			retainUserInputTransform: mutateADFField,
		},

		[COMPONENTS_FIELD]: {
			Component: ComponentsField,
			defaultValueKey: 'selectedComponents',
			allowedValuesKey: 'components.nodes',
			submitTransform: mapComponentOptionToData,
			retainUserInputTransform: mutateComponentsField,
		},
		[DATA_CLASSIFICATION_FIELD]: {
			Component: DataClassificationField,
			defaultValueKey: ['classificationLevel', 'defaultClassificationLevel'],
			allowedValuesKey: 'classificationLevels.edges',
		},
		[USER_PICKER_FIELD]: {
			Component: SingleUserPickerField,
			defaultValueKey: 'user',
			submitTransform: mapUserOptionsToIds,
			retainUserInputTransform: mutateSingleUserPicker,
		},
		[FORGE_USER_PICKER_FIELD]: {
			Component: SingleUserPickerField,
			defaultValueKey: 'user',
			submitTransform: mapUserOptionsToAccountIds,
			retainUserInputTransform: mutateSingleUserPicker,
		},
		[MULTI_USER_PICKER_FIELD]: {
			Component: MultiUserPickerField,
			defaultValueKey: 'selectedUsers',
			submitTransform: mapUserOptionsToIds,
			retainUserInputTransform: mutateMultiUserPicker,
		},
		[JSM_MULTI_USER_PICKER_FIELD]: {
			Component: MultiUserPickerField,
			defaultValueKey: 'selectedUsers',
			submitTransform: mapUserOptionsToIds,
			retainUserInputTransform: mutateMultiUserPicker,
		},
		[FORGE_MULTI_USER_PICKER_FIELD]: {
			Component: MultiUserPickerField,
			defaultValueKey: 'selectedUsers',
			submitTransform: mapUserOptionsToAccountIds,
			retainUserInputTransform: mutateMultiUserPicker,
		},
		[PEOPLE_FIELD]: {
			Component: PeopleField,
			defaultValueKey: 'selectedUsers',
			submitTransform: mapUserOptionOrOptionsToIds,
			retainUserInputTransform: mutatePeopleField,
		},
		[JSM_PEOPLE_FIELD]: {
			Component: PeopleField,
			defaultValueKey: 'selectedUsers',
			submitTransform: mapUserOptionOrOptionsToIds,
			retainUserInputTransform: mutatePeopleField,
		},
		[NUMBER_FIELD]: {
			Component: NumberField,
			defaultValueKey: 'number',
			submitTransform: transformAsNumber,
			retainUserInputTransform: mutateNumberField,
		},
		[CONNECT_NUMBER_FIELD]: {
			Component: NumberField,
			defaultValueKey: 'number',
			submitTransform: transformAsNumber,
			retainUserInputTransform: mutateNumberField,
		},
		[FORGE_NUMBER_FIELD]: {
			Component: NumberField,
			defaultValueKey: 'number',
			submitTransform: transformAsNumber,
			retainUserInputTransform: mutateNumberField,
		},
		[DATE_TIME_FIELD]: {
			Component: DateTimeField,
			defaultValueKey: 'dateTime',
			submitTransform: transformDate,
			retainUserInputTransform: mutateDateTimeField,
		},
		[FORGE_DATETIME_FIELD]: {
			Component: DateTimeField,
			defaultValueKey: 'dateTime',
			submitTransform: transformDate,
			retainUserInputTransform: mutateDateTimeField,
		},
		[EPIC_LINK_FIELD]: {
			Component: EpicLinkField,
			defaultValueKey: 'epic',
			submitTransform: mapEpicLinkOptionToData,
			retainUserInputTransform: mutateEpicLinkField,
		},
		[LABEL_FIELD]: {
			Component: LabelsField,
			defaultValueKey: 'selectedLabels',
			submitTransform: mapLabelOptionsToData,
			retainUserInputTransform: mutateLabelsFields,
		},
		[FORGE_STRINGS_FIELD]: {
			Component: ForgeLabelsField,
			defaultValueKey: 'selectedLabels',
			submitTransform: mapLabelOptionsToData,
			retainUserInputTransform: mutateLabelsFields,
		},
		[ASSET_FIELD]: {
			Component: AssetField,
			defaultValueKey: 'selectedAssets',
			submitTransform: mapAssetOptionsToAssetEntity,
			retainUserInputTransform: mutateAssetsFields,
		},
		[VERSION_FIELD]: {
			Component: SingleVersionField,
			defaultValueKey: 'version',
			allowedValuesKey: 'versions.nodes',
			submitTransform: getVersionId,
			retainUserInputTransform: mutateSingleVersionField,
		},
		[MULTI_VERSION_FIELD]: {
			Component: MultiVersionsField,
			defaultValueKey: 'selectedVersions',
			allowedValuesKey: 'versions.nodes',
			submitTransform: getVersionsIds,
			retainUserInputTransform: mutateMultiVersionsField,
		},
		[PRIORITY_FIELD]: {
			Component: PriorityField,
			defaultValueKey: 'priority',
			allowedValuesKey: 'priorities.nodes',
			submitTransform: mapPriorityOptionToData,
			retainUserInputTransform: mutatePriorityField,
		},
		[DATE_PICKER_FIELD]: {
			Component: DateField,
			defaultValueKey: 'date',
			submitTransform: transformDate,
			retainUserInputTransform: mutateDateField,
		},
		[FORGE_DATE_FIELD]: {
			Component: DateField,
			defaultValueKey: 'date',
			submitTransform: transformDate,
			retainUserInputTransform: mutateDateField,
		},
		[SECURITY_FIELD]: {
			Component: SecurityLevelField,
			defaultValueKey: 'securityLevel',
			allowedValuesKey: 'securityLevels.nodes',
			submitTransform: transformOnSubmit,
			retainUserInputTransform: mutateSecurityLevelField,
		},
		[ISSUE_RESTRICTION_FIELD]: {
			Component: IssueRestrictionField,
			defaultValueKey: 'selectedRoles',
			submitTransform: mapRoleToRestrictionValue,
		},
		[URL_FIELD]: {
			Component: UrlField,
			defaultValueKey: 'url',
			retainUserInputTransform: mutateUrlField,
		},
		[SINGLE_SELECT_FIELD]: {
			Component: SingleSelectField,
			defaultValueKey: 'fieldOption',
			allowedValuesKey: 'fieldOptions.nodes',
			submitTransform: mapSelectOptionsToData,
			retainUserInputTransform: mutateSingleSelectField,
		},
		[CONNECT_SINGLE_SELECT_FIELD]: {
			Component: SingleSelectField,
			defaultValueKey: 'fieldOption',
			allowedValuesKey: 'fieldOptions.nodes',
			submitTransform: mapSelectOptionsToConnectData,
			retainUserInputTransform: mutateSingleSelectField,
		},
		[MULTI_SELECT_FIELD]: {
			Component: MultiSelectField,
			defaultValueKey: 'selectedFieldOptions',
			allowedValuesKey: 'fieldOptions.nodes',
			submitTransform: mapMultiSelectOptionsToData,
			retainUserInputTransform: mutateMultiSelectField,
		},
		[CONNECT_MULTI_SELECT_FIELD]: {
			Component: MultiSelectField,
			defaultValueKey: 'selectedFieldOptions',
			allowedValuesKey: 'fieldOptions.nodes',
			submitTransform: mapMultiSelectOptionsToConnectData,
			retainUserInputTransform: mutateMultiSelectField,
		},
		[SINGLE_GROUP_PICKER_FIELD]: {
			Component: SingleGroupPickerField,
			defaultValueKey: 'selectedGroup',
			submitTransform: transformGroupToName,
			retainUserInputTransform: mutateSingleGroupPickerField,
		},
		[FORGE_GROUP_PICKER_FIELD]: {
			Component: SingleGroupPickerField,
			defaultValueKey: 'selectedGroup',
			submitTransform: transformGroupToName,
			retainUserInputTransform: mutateSingleGroupPickerField,
		},
		[MUTLI_GROUP_PICKER_FIELD]: {
			Component: MultiGroupPickerField,
			defaultValueKey: 'selectedGroups',
			submitTransform: transformGroupsToNames,
			retainUserInputTransform: mutateMultipleGroupPickerField,
		},
		[FORGE_MULTI_GROUP_PICKER_FIELD]: {
			Component: MultiGroupPickerField,
			defaultValueKey: 'selectedGroups',
			submitTransform: transformGroupsToNames,
			retainUserInputTransform: mutateMultipleGroupPickerField,
		},
		[SPRINT_FIELD]: {
			Component: SprintField,
			defaultValueKey: 'selectedSprints',
			submitTransform: mapSprintOptionToData,
			retainUserInputTransform: mutateJiraSprintField,
		},
		[ISSUE_LINK_FIELD]: {
			Component: IssueLinksField,
			submitTransform: mapOptionsToData,
			defaultValueKey: 'issueLinks',
			allowedValuesKey: 'issueLinkTypeRelations.nodes',
			retainUserInputTransform: mutateIssueLinksField,
		},
		[COLOR_FIELD]: {
			Component: ColorField,
			defaultValueKey: 'color.colorKey',
			submitTransform: asServerColor,
			retainUserInputTransform: mutateIssueColorField,
		},
		[EPIC_COLOR_FIELD]: {
			Component: ColorField,
			defaultValueKey: 'color.colorKey',
			submitTransform: asClassicEpicColor,
			retainUserInputTransform: mutateEpicColorField,
		},
		[TEXT_AREA_FIELD]: {
			Component: TextAreaField,
			defaultValueKey: 'richText.plainText',
			submitTransform: convertTextToAdf,
			retainUserInputTransform: mutateTextAreaField,
		},
		[FORGE_OBJECT_FIELD]: {
			Component: TextAreaField,
			defaultValueKey: 'object',
			submitTransform: transformJSONStringToObject,
			retainUserInputTransform: mutateTextAreaField,
		},
		[ASSIGNEE_FIELD]: {
			Component: AssigneeField,
			defaultValueKey: 'user',
			submitTransform: mapUserToAssigneeValue,
			retainUserInputTransform: mutateAssigneeUserPicker,
		},
		[REPORTER_FIELD]: {
			Component: ReporterField,
			defaultValueKey: 'user',
			submitTransform: mapUserOptionsToIds,
			retainUserInputTransform: mutateSingleUserPicker,
		},
		[RESOLUTION_FIELD]: {
			Component: ResolutionField,
			defaultValueKey: 'resolution',
			allowedValuesKey: 'resolutions.nodes',
			submitTransform: mapOptionToResolutionOption,
			retainUserInputTransform: mutateResolutionField,
		},
		[ATTACHMENTS_FIELD]: {
			Component: AttachmentsField,
			submitTransform: getFileIdsFromFiles,
		},
		[CASCADING_SELECT_FIELD]: {
			Component: CascadingSelectField,
			defaultValueKey: 'cascadingOption',
			allowedValuesKey: 'cascadingOptions.nodes',
			submitTransform: transformToServerValue,
			retainUserInputTransform: mutateCascadingSelectField,
		},
		[TEAM_FIELD]: {
			Component: CustomTeamsField,
			defaultValueKey: 'selectedTeam',
			submitTransform: transformTeamFieldTypeString,
			retainUserInputTransform: mutateTeamField,
		},
		[ATLASSIAN_TEAM_FIELD]: {
			Component: AtlassianTeamsField,
			defaultValueKey: 'JiraAtlassianTeamField',
			submitTransform: transformAtlassianTeamFieldTypeString,
			retainUserInputTransform: mutateAtlassianTeamField,
		},
		[CMDB_FIELD]: {
			Component: CmdbField,
			defaultValueKey: 'selectedCmdbObjects',
			submitTransform: transformCmdbField,
			retainUserInputTransform: null,
		},
		[ORGANIZATIONS_FIELD]: {
			Component: OrganizationsField,
			defaultValueKey: 'selectedOrganizations',
			submitTransform: mapOrganizationOptionsToData,
			retainUserInputTransform: mutateOrganizationsField,
		},
		[MESSAGE_EDIT_FIELD]: {
			Component: MessageField,
			defaultValueKey: 'richText.adfValue.json',
		},
		[PROJECT_FIELD]: {
			Component: ProjectField,
			defaultValueKey: 'project',
			submitTransform: mapProjectOptionToData,
			retainUserInputTransform: mutateProjectField,
		},
		[TIME_TRACKING_FIELD]: {
			Component: TimeTrackingField,
			submitTransform: transformTimeTrackingValueToSubmitFormat,
			retainUserInputTransform: mutateTimeTrackingField,
			defaultValueKey: 'originalEstimate',
		},
		[LOG_WORK_FIELD]: {
			Component: LogWorkField,
			submitTransform: getLogWorkSubmitValue,
		},
		[CHECKBOX_FIELD]: {
			Component: CheckboxField,
			allowedValuesKey: 'fieldOptions.nodes',
			defaultValueKey: 'selectedFieldOptions',
			submitTransform: mapCheckboxOptionsToData,
			retainUserInputTransform: mutateCheckboxField,
		},
		[PARENT_LINK_FIELD]: {
			Component: ParentLinkField,
			defaultValueKey: 'parentIssue',
			submitTransform: mapParentLinkOptionToData,
		},
		[PARENT_FIELD]: {
			Component: ParentField,
			defaultValueKey: 'parentIssue',
			submitTransform: mapParentOptionToData,
			retainUserInputTransform: mutateParentField,
		},
		...(ff('jsm-responders-field-in-gicx-ui')
			? {
					[RESPONDERS_FIELD]: {
						Component: RespondersField,
						defaultValueKey: 'selectedUsers',
						submitTransform: mapUserOptionsToAris,
					},
				}
			: {}),

		[RADIO_FIELD]: {
			Component: RadioGroupField,
			defaultValueKey: 'selectedOption',
			allowedValuesKey: 'fieldOptions.nodes',
			submitTransform: mapRadioGroupOptionToData,
			retainUserInputTransform: mutateSingleSelectField,
		},
		[ENTITLEMENT_FIELD]: {
			Component: EntitlementField,
			defaultValueKey: 'selectedEntitlement',
			submitTransform: mapEntitlementOptionToData,
			retainUserInputTransform: undefined,
		},
		...(fg('jcs_issue_layout_eap')
			? {
					[ORGANIZATION_FIELD]: {
						Component: OrganizationField,
						defaultValueKey: 'selectedOrganization',
						submitTransform: mapOrganizationOptionToData,
						retainUserInputTransform: undefined,
					},
				}
			: {}),
	}) as const;

export const getGqlFieldMap = memoizeOne<typeof createFieldMapItems>(createFieldMapItems);

export const UPDATE_FIELDS_CREATE_LINKED_ISSUE = [
	ISSUE_LINKS_TYPE,
	WORK_LOG_TYPE,
	ATTACHMENT_TYPE,
] as const;

export const UPDATE_FIELDS = [ISSUE_LINKS_TYPE, WORK_LOG_TYPE] as const;

export const CONTEXT_FIELD_TYPES = [PROJECT_FIELD, ISSUE_TYPE_FIELD, REQUEST_TYPE_FIELD] as const;
